/**
 * /payroll/earning-codes — Payroll earning code configuration.
 *
 * Shows all earning codes for the entity with taxability flags and
 * GL account mapping. Earning codes drive which taxes apply to each
 * pay type (OT, holiday, bonus, etc.) and where the expense posts.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Box, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import { api } from "@/lib/api";
import { queryOptions } from "@tanstack/react-query";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/earning-codes")({
  component: EarningCodesPage,
});

interface EarningCode {
  id: string;
  code: string;
  name: string;
  taxable_federal: boolean;
  taxable_state: boolean;
  taxable_fica: boolean;
  taxable_futa: boolean;
  include_in_ot_calc: boolean;
  gl_account_code: string | null;
  is_active: boolean;
}

const earningCodesQueryOptions = () =>
  queryOptions({
    queryKey: ["earning-codes"],
    queryFn: () => api<EarningCode[]>("/api/payroll/earning-codes"),
  });

function TaxFlag({ value }: { value: boolean }) {
  return value ? (
    <Chip label="✓" size="small" color="success" variant="outlined" sx={{ minWidth: 32 }} />
  ) : (
    <Typography variant="body2" color="text.disabled">—</Typography>
  );
}

function EarningCodesPage() {
  const q = useQuery(earningCodesQueryOptions());
  const codes = q.data ?? [];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <CodeIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>Earning Codes</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          Pay types · taxability flags · GL mapping
        </Typography>
        <Box sx={{ ml: "auto" }}>
          <HelpHint docPath="payroll-l1/codes.md" label="Earning codes help" />
        </Box>
      </Box>

      {q.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" } }}>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="center">Federal Tax</TableCell>
                <TableCell align="center">State Tax</TableCell>
                <TableCell align="center">FICA</TableCell>
                <TableCell align="center">FUTA</TableCell>
                <TableCell align="center">OT Calc</TableCell>
                <TableCell>GL Account</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {codes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    No earning codes configured. They are seeded during payroll setup.
                  </TableCell>
                </TableRow>
              )}
              {codes.map((ec) => (
                <TableRow key={ec.id} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>{ec.code}</TableCell>
                  <TableCell>{ec.name}</TableCell>
                  <TableCell align="center"><TaxFlag value={ec.taxable_federal} /></TableCell>
                  <TableCell align="center"><TaxFlag value={ec.taxable_state} /></TableCell>
                  <TableCell align="center"><TaxFlag value={ec.taxable_fica} /></TableCell>
                  <TableCell align="center"><TaxFlag value={ec.taxable_futa} /></TableCell>
                  <TableCell align="center"><TaxFlag value={ec.include_in_ot_calc} /></TableCell>
                  <TableCell sx={{ fontFamily: "monospace", color: "text.secondary", fontSize: 12 }}>
                    {ec.gl_account_code ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ec.is_active ? "Active" : "Inactive"}
                      size="small"
                      color={ec.is_active ? "success" : "default"}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
